import {
  useNode,
  useStructuredWorkflow,
  useUpdateNode,
} from "@/api/workflow-hooks";
import { Button } from "@/components/button";
import { Checkbox } from "@/components/checkbox";
import { Label } from "@/components/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/tabs";
import {
  type ActionNode,
  type SendEmailActionNode,
  isExportNode,
} from "@repo/shared/workflow";
import { produce } from "immer";
import { useCallback, useEffect, useState } from "react";

export const SendEmailEditor: React.FC<{
  workflowId: string;
  actionId: string;
}> = ({ workflowId, actionId }) => {
  const [wasEdited, setWasEdited] = useState(false);
  const [action, setAction] = useState<SendEmailActionNode | null>(null);
  const { data: _action } = useNode(workflowId, actionId);
  const updateNodeMut = useUpdateNode(workflowId);
  const { workflow, actionNodes } = useStructuredWorkflow(workflowId);

  useEffect(() => {
    if (_action && !action) setAction(_action as SendEmailActionNode);
  }, [_action, action]);

  const handleUpdate = useCallback((update: SendEmailActionNode) => {
    setAction(update);
    setWasEdited(true);
  }, []);

  const handleSave = useCallback(async () => {
    if (wasEdited && action) {
      await updateNodeMut.mutateAsync({
        nodeId: action.id,
        update: action,
      });

      setWasEdited(false);
    }
  }, [wasEdited, action, updateNodeMut]);

  return (
    <div className="max-w-[1080px] p-4 gap-y-8">
      <Label className="block mt-2 mb-2 font-medium pb-2">Email</Label>
      <div className="rounded border p-4">
        <Label className="block mb-2 font-medium pb-2">
          Include attachments
        </Label>
        <div className="space-y-4 flex flex-col">
          {actionNodes
            .filter((n) => isExportNode(n))
            ?.map((exportNode) => {
              return (
                <div
                  key={exportNode.id}
                  className="flex items-center space-x-2"
                >
                  <Checkbox
                    id={`group-${exportNode.id}`}
                    checked={action?.dependencyIds?.includes(exportNode.id)}
                    onCheckedChange={(checked) => {
                      if (!action) return;

                      const newAction = produce(action, (draft) => {
                        if (
                          checked &&
                          !draft.dependencyIds.includes(exportNode.id)
                        ) {
                          draft.dependencyIds.push(exportNode.id);
                        } else if (!checked) {
                          draft.dependencyIds = draft.dependencyIds.filter(
                            (id) => id !== exportNode.id,
                          );
                        }
                      });

                      handleUpdate(newAction);
                    }}
                  />
                  <Label htmlFor={`group-${exportNode.id}`}>
                    {exportNode.name}
                  </Label>
                </div>
              );
            })}
        </div>
      </div>
      <div className="rounded border p-4 my-2">
        <Label className="block mb-2 font-medium pb-2">Respond to</Label>
        <div>
          Attachments will be sent as a response to the email triggering this
          workflow.
        </div>
      </div>
      <div className="rounded border p-4 mb-2">
        <Label className="block mb-2 font-medium pb-2">Run Mode</Label>
        <Tabs
          value={action?.runMode}
          onValueChange={(value: string) => {
            if (!action) return;
            const newAction = produce(action, (draft) => {
              draft.runMode = value as "automatic" | "manual";
            });
            handleUpdate(newAction);
          }}
        >
          <TabsList className="grid w-fit grid-cols-2">
            <TabsTrigger value="manual" disabled>
              Manual
            </TabsTrigger>
            <TabsTrigger value="automatic">Automatic</TabsTrigger>
          </TabsList>
          <TabsContent value="manual">
            <p className="text-sm text-muted-foreground">
              The export will need to be manually run by a person.
            </p>
          </TabsContent>
          <TabsContent value="automatic">
            <p className="text-sm text-muted-foreground">
              Only automatic emails can be sent for now.
            </p>
          </TabsContent>
        </Tabs>
      </div>
      <div className="flex justify-end mt-4">
        <Button
          disabled={!wasEdited || updateNodeMut.isPending}
          onClick={handleSave}
        >
          Save
        </Button>
      </div>
    </div>
  );
};
